<template>
  <div>
    <v-card class="card-shadow border-radius-xl">
      <v-card-title>
        <v-row align="center">
          <v-col cols="12" class="d-flex justify-space-between align-center">
            <h5 class="mb-0 text-h5 text-typo font-weight-bold">員工排班</h5>
            <v-btn-toggle v-model="viewMode" mandatory dense>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn value="list" large v-bind="attrs" v-on="on">
                    <v-icon>mdi-format-list-bulleted</v-icon>
                    <span class="ml-1">列表</span>
                  </v-btn>
                </template>
                <span>列表顯示</span>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn value="grid" large v-bind="attrs" v-on="on">
                    <v-icon>mdi-grid</v-icon>
                    <span class="ml-1">格狀</span>
                  </v-btn>
                </template>
                <span>格狀顯示</span>
              </v-tooltip>
            </v-btn-toggle>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-subtitle>
        <!-- Search Controls -->
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              hide-details
              class="
                input-style
                font-size-input
                text-light-input
                placeholder-light
                input-icon
              "
              dense
              flat
              filled
              solo
              height="43"
              v-model="search"
              placeholder="搜尋員工姓名"
              clearable
              @input="onSearchInput"
            >
              <template slot="prepend-inner">
                <v-icon color="#adb5bd" size=".875rem">fas fa-search</v-icon>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="clockSettingFilterId"
              :items="clockSettingFilterItems"
              item-text="name"
              item-value="id"
              hide-details
              dense
              outlined
              class="
                mt-0
                input-style
                font-size-input
                text-light-input
                placeholder-light
                input-icon
              "
              label="打卡設定"
              @change="onClockSettingFilterChange"
            ></v-select>
          </v-col>
          <v-col cols="12" md="5" class="d-flex justify-end align-center">
            <span v-if="selectedItems.length > 0" class="text-body-2 mr-3">
              已選擇 {{ selectedItems.length }} 位員工
            </span>
            <v-btn
              color="secondary"
              elevation="0"
              :ripple="false"
              height="43"
              :disabled="selectedItems.length === 0"
              @click="selectedItems = []"
              class="
                font-weight-bold
                text-capitalize
                btn-secondary
                bg-gradient-light
                py-3
                px-6
                me-2
              "
            >
              清除
            </v-btn>
            <v-btn
              color="primary"
              elevation="0"
              :ripple="false"
              height="43"
              :disabled="selectedItems.length === 0"
              @click="openBatchUpdateDialog"
              class="
                font-weight-bold
                text-capitalize
                btn-primary
                bg-gradient-primary
                py-3
                px-6
              "
            >
              <v-icon left>mdi-pencil</v-icon>
              變更排班
            </v-btn>
          </v-col>
        </v-row>
      </v-card-subtitle>
      <v-card-text class="px-0 py-0">
        <!-- List View -->
        <v-data-table
          v-if="viewMode === 'list'"
          :headers="headers"
          :items="employeeClockSettings"
          :loading="loading"
          :server-items-length="totalItems"
          v-model="selectedItems"
          show-select
          item-key="id"
          class="table"
          hide-default-footer
          @page-count="pageCount = $event"
          disable-pagination
          :items-per-page="itemsPerPage"
          mobile-breakpoint="0"
        >
          <template v-slot:item.user_name="{ item }">
            <div class="d-flex align-center">
              <span class="text-sm font-weight-normal text-body">
                {{ item.user ? item.user.name : "-" }}
              </span>
            </div>
          </template>

          <template v-slot:item.clock_setting_name="{ item }">
            <span class="text-sm font-weight-normal text-body">
              {{
                item.companyClockSetting ? item.companyClockSetting.name : "-"
              }}
            </span>
          </template>

          <template v-slot:item.fixed_working_days="{ item }">
            <v-chip
              x-small
              :color="item.fixed_working_days ? 'blue' : 'green'"
              text-color="white"
              class="px-2"
            >
              {{ item.fixed_working_days ? "固定" : "排班" }}
            </v-chip>
          </template>

          <template v-slot:item.clock_in_time="{ item }">
            <span class="text-sm font-weight-normal text-body">
              {{ item.clock_in_time || "-" }}
            </span>
          </template>

          <template v-slot:item.clock_out_time="{ item }">
            <span class="text-sm font-weight-normal text-body">
              {{ item.clock_out_time || "-" }}
            </span>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              @click="editItem(item)"
              icon
              elevation="0"
              :ripple="false"
              height="28"
              min-width="36"
              width="36"
              class="btn-ls me-2 my-2 rounded-sm"
              color="#67748e"
              title="編輯"
            >
              <v-icon size="18">mdi-pencil</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <!-- Grid View -->
        <div v-if="viewMode === 'grid'" class="grid-view-container pa-4">
          <v-row>
            <v-col
              v-for="(item, index) in employeeClockSettings"
              :key="index"
              cols="12"
              sm="6"
              md="4"
              class="mb-4"
            >
              <v-card
                class="employee-clock-card h-100 card-shadow border-radius-xl"
                :class="{ 'border-primary': isItemSelected(item) }"
                @click="toggleItemSelection(item)"
                style="cursor: pointer; border: 1px solid transparent"
              >
                <v-card-text class="pa-4">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <div class="d-flex align-center">
                      <v-checkbox
                        :input-value="isItemSelected(item)"
                        hide-details
                        class="ma-0 pa-0 mr-2"
                        @click.stop="toggleItemSelection(item)"
                      ></v-checkbox>
                      <h3 class="font-weight-bold mb-0">
                        {{ item.user ? item.user.name : "-" }}
                      </h3>
                    </div>
                    <v-chip
                      x-small
                      :color="item.fixed_working_days ? 'blue' : 'green'"
                      text-color="white"
                      class="px-2"
                    >
                      {{ item.fixed_working_days ? "固定" : "排班" }}
                    </v-chip>
                  </div>

                  <div class="mt-4">
                    <div class="d-flex align-center mb-2">
                      <v-icon size="18" color="grey" class="mr-2"
                        >mdi-clock-outline</v-icon
                      >
                      <span class="text-body-2">
                        打卡設定:
                        {{
                          item.companyClockSetting
                            ? item.companyClockSetting.name
                            : "-"
                        }}
                      </span>
                    </div>

                    <div class="d-flex align-center mb-2">
                      <v-icon size="18" color="grey" class="mr-2"
                        >mdi-login</v-icon
                      >
                      <span class="text-body-2"
                        >上班: {{ item.clock_in_time || "-" }}</span
                      >
                    </div>

                    <div class="d-flex align-center mb-2">
                      <v-icon size="18" color="grey" class="mr-2"
                        >mdi-logout</v-icon
                      >
                      <span class="text-body-2"
                        >下班: {{ item.clock_out_time || "-" }}</span
                      >
                    </div>
                  </div>

                  <div class="mt-3 pt-2 border-top d-flex justify-end">
                    <v-btn
                      icon
                      small
                      color="#67748e"
                      @click.stop="editItem(item)"
                    >
                      <v-icon size="18">mdi-pencil</v-icon>
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
          <v-row v-if="employeeClockSettings.length === 0 && !loading">
            <v-col cols="12" class="text-center">
              <p class="text-body">找不到相關員工</p>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
      <v-card-actions class="card-padding">
        <v-row>
          <v-col cols="12" class="ml-auto d-flex justify-center">
            <v-pagination
              prev-icon="fa fa-angle-left"
              next-icon="fa fa-angle-right"
              class="pagination"
              color="#cb0c9f"
              v-model="page"
              :length="pageCount"
              @input="onPageChange"
              circle
            ></v-pagination>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>

    <!-- Batch Update Dialog -->
    <v-dialog v-model="batchUpdateDialog" max-width="600px">
      <v-card class="card-shadow border-radius-xl">
        <div class="card-header-padding card-border-bottom">
          <span class="font-weight-bold text-h5 text-typo mb-0"
            >變更排班設定</span
          >
        </div>
        <v-card-text class="card-padding">
          <p class="text-body-1 mb-4">
            您已選擇
            <strong>{{ selectedItems.length }}</strong>
            位員工，請選擇要套用的打卡設定：
          </p>
          <v-select
            v-model="selectedClockSettingId"
            :items="companyClockSettings"
            item-text="name"
            item-value="id"
            label="選擇打卡設定"
            outlined
            dense
            class="mt-4"
          >
            <template v-slot:item="{ item }">
              <div class="py-2">
                <div class="font-weight-bold">{{ item.name }}</div>
                <div class="text-caption text-grey">{{ item.description }}</div>
              </div>
            </template>
          </v-select>
        </v-card-text>
        <v-card-actions class="card-padding d-flex justify-end">
          <v-btn
            @click="closeBatchUpdateDialog"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-secondary
              bg-gradient-light
              py-3
              px-6
              me-2
            "
          >
            取消
          </v-btn>
          <v-btn
            @click="batchUpdateClockSetting"
            elevation="0"
            :ripple="false"
            height="43"
            :disabled="!selectedClockSettingId"
            :loading="updating"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-primary
              bg-gradient-primary
              py-3
              px-6
            "
          >
            確定變更
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Progress Dialog -->
    <progress-dialog :visible="loading" message="載入中，請稍候..." />

    <!-- Info Dialog -->
    <info-dialog
      :visible.sync="infoDialog.visible"
      :title="infoDialog.title"
      :message="infoDialog.message"
      yes-button-text="確定"
      @yes-clicked="onInfoDialogYes"
    />
  </div>
</template>

<script>
import ProgressDialog from "@/components/Common/ProgressDialog.vue";
import InfoDialog from "@/components/Common/InfoDialog.vue";

export default {
  name: "EmployeeClockSettings",
  components: {
    ProgressDialog,
    InfoDialog,
  },
  props: {
    companyId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
      employeeClockSettings: [],
      search: "",
      searchDebounceTimer: null,
      loading: false,
      totalItems: 0,
      selectedItems: [],
      viewMode: "list",
      batchUpdateDialog: false,
      selectedClockSettingId: null,
      companyClockSettings: [],
      clockSettingFilterId: null,
      updating: false,
      infoDialog: {
        visible: false,
        title: "",
        message: "",
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 1,
      },
      headers: [
        {
          text: "員工姓名",
          align: "start",
          sortable: false,
          value: "user_name",
          class: "text-secondary font-weight-bolder opacity-7",
        },
        {
          text: "打卡設定",
          value: "clock_setting_name",
          class: "text-secondary font-weight-bolder opacity-7",
        },
        {
          text: "工作日類型",
          value: "fixed_working_days",
          class: "text-secondary font-weight-bolder opacity-7",
        },
        {
          text: "上班時間",
          value: "clock_in_time",
          class: "text-secondary font-weight-bolder opacity-7",
        },
        {
          text: "下班時間",
          value: "clock_out_time",
          class: "text-secondary font-weight-bolder opacity-7",
        },
        // {
        //   text: "操作",
        //   value: "actions",
        //   sortable: false,
        //   align: "center",
        //   class: "text-secondary font-weight-bolder opacity-7",
        // },
      ],
    };
  },
  watch: {
    // watch companyId changes
    async companyId() {
      console.log("companyId changed");
      await this.fetchCompanyClockSettings();
      await this.fetchEmployeeClockSettings();
    },
  },
  computed: {
    clockSettingFilterItems() {
      const base = [{ id: null, name: "全部" }];
      const mapped = this.companyClockSettings.map((setting) => ({
        id: setting.id,
        name: setting.name,
      }));
      return base.concat(mapped);
    },
  },
  async mounted() {
    if (this.$vuetify.breakpoint.mobile) {
      this.viewMode = "grid";
    }
    try {
      await this.fetchCompanyClockSettings();
      await this.fetchEmployeeClockSettings();
    } catch (error) {
      console.error("Error in mounted:", error);
      alert("Failed to initialize component");
    }
  },
  beforeDestroy() {
    // Clean up debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },
  methods: {
    async onClockSettingFilterChange() {
      this.page = 1;
      this.fetchEmployeeClockSettings();
    },
    async fetchEmployeeClockSettings() {
      try {
        this.loading = true;

        // Build query parameters
        let queryParams = `page=${this.page}&limit=${this.itemsPerPage}`;

        if (this.companyId) {
          queryParams += `&company_id=${this.companyId}`;
        }

        if (this.search && this.search.trim()) {
          queryParams += `&name=${encodeURIComponent(this.search.trim())}`;
        }

        if (this.clockSettingFilterId) {
          queryParams += `&company_clock_setting_id=${this.clockSettingFilterId}`;
        }

        // Use axios to fetch employee clock settings
        const response = await this.$axios.get(
          `/api/employee-clock-settings?${queryParams}`
        );

        this.employeeClockSettings = response.data.data;
        this.pagination = response.data.pagination;
        this.pageCount = this.pagination.totalPages;
        this.totalItems = this.pagination.totalItems;
        console.log(
          "Employee clock settings loaded:",
          this.employeeClockSettings
        );
      } catch (error) {
        console.error("Error fetching employee clock settings:", error);
        alert("載入員工打卡設定失敗");
      } finally {
        this.loading = false;
      }
    },

    onSearchInput() {
      // Clear existing timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      // Set new timer for 500ms debounce
      this.searchDebounceTimer = setTimeout(() => {
        this.page = 1; // Reset to first page when searching
        this.fetchEmployeeClockSettings();
      }, 500);
    },

    async onPageChange(newPage) {
      this.page = newPage;
      await this.fetchEmployeeClockSettings();
    },

    async fetchCompanyClockSettings() {
      try {
        if (!this.companyId) return;

        const response = await this.$axios.get(
          `/api/company-clock-settings/${this.companyId}`
        );
        this.companyClockSettings = response.data;
        console.log(
          "Company clock settings loaded:",
          this.companyClockSettings
        );
      } catch (error) {
        console.error("Error fetching company clock settings:", error);
        alert("載入打卡設定失敗");
      }
    },

    async openBatchUpdateDialog() {
      if (this.selectedItems.length === 0) {
        alert("請先選擇要變更的員工");
        return;
      }
      await this.fetchCompanyClockSettings();
      this.batchUpdateDialog = true;
      await this.$nextTick();
    },

    closeBatchUpdateDialog() {
      this.batchUpdateDialog = false;
      this.selectedClockSettingId = null;
    },

    async batchUpdateClockSetting() {
      if (!this.selectedClockSettingId) {
        alert("請選擇打卡設定");
        return;
      }

      try {
        this.updating = true;

        const payload = {
          employee_clock_setting_ids: this.selectedItems.map((item) => item.id),
          company_clock_setting_id: this.selectedClockSettingId,
        };

        console.log("Batch update payload:", payload);

        const response = await this.$axios.post(
          "/api/employee-clock-settings/batch-update-company-setting",
          payload
        );

        console.log("Batch update response:", response.data);

        // Close dialog
        this.closeBatchUpdateDialog();

        // Show success message
        this.infoDialog.title = "操作成功";
        this.infoDialog.message = `已成功更新 ${response.data.updated_count} 位員工的排班設定`;
        this.infoDialog.visible = true;

        // Clear selections
        this.selectedItems = [];

        // Re-initialize daily clock settings after successful update
        await this.init_clock();
      } catch (error) {
        console.error("Error batch updating clock settings:", error);
        this.infoDialog.title = "操作失敗";
        this.infoDialog.message =
          error.response?.data?.error || "批次更新排班設定失敗";
        this.infoDialog.visible = true;
      } finally {
        this.updating = false;
      }
    },

    async onInfoDialogYes() {
      await this.fetchEmployeeClockSettings();
    },

    async init_clock(clean = false) {
      const payload = { company_id: this.companyId };
      if (clean) {
        payload.clean = true;
      }
      const response = await this.$axios.post(
        "/api/employee-clock-settings/reset-init-daily",
        payload
      );
      return response.data;
    },

    editItem(item) {
      // TODO: Implement edit functionality
      console.log("Edit item:", item);
      alert("編輯功能尚未實作");
    },
    isItemSelected(item) {
      return this.selectedItems.some((selected) => selected.id === item.id);
    },
    toggleItemSelection(item) {
      const index = this.selectedItems.findIndex(
        (selected) => selected.id === item.id
      );
      if (index > -1) {
        this.selectedItems.splice(index, 1);
      } else {
        this.selectedItems.push(item);
      }
    },
  },
};
</script>

<style scoped>
.table {
  border-radius: 0.75rem;
}
</style>
